<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  singleSelect: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const store = useStore();
const { t } = useI18n();

const selectedIds = computed({
  get: () => props.modelValue,
  set: val => emit('update:modelValue', val),
});

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const toggleInbox = inboxId => {
  if (props.singleSelect) {
    // Single select mode: toggle or replace
    selectedIds.value = selectedIds.value.includes(inboxId) ? [] : [inboxId];
    return;
  }
  const idx = selectedIds.value.indexOf(inboxId);
  if (idx > -1) {
    selectedIds.value = selectedIds.value.filter(id => id !== inboxId);
  } else {
    selectedIds.value = [...selectedIds.value, inboxId];
  }
};

const selectAll = () => {
  if (props.singleSelect) return;
  selectedIds.value = inboxes.value.map(i => i.id);
};

const deselectAll = () => {
  selectedIds.value = [];
};

const getInboxIcon = inbox => {
  const type = inbox.channel_type || '';
  if (type.includes('whatsapp')) return 'i-lucide-message-circle';
  if (type.includes('facebook')) return 'i-lucide-facebook';
  if (type.includes('instagram')) return 'i-lucide-instagram';
  if (type.includes('email')) return 'i-lucide-mail';
  if (type.includes('web')) return 'i-lucide-globe';
  if (type.includes('telegram')) return 'i-lucide-send';
  if (type.includes('sms')) return 'i-lucide-smartphone';
  return 'i-lucide-inbox';
};

const getInboxColor = inbox => {
  const type = inbox.channel_type || '';
  if (type.includes('whatsapp')) return 'text-green-500';
  if (type.includes('facebook')) return 'text-blue-600';
  if (type.includes('instagram')) return 'text-pink-500';
  if (type.includes('email')) return 'text-amber-500';
  if (type.includes('telegram')) return 'text-sky-500';
  return 'text-n-slate-11';
};
</script>

<template>
  <div class="space-y-6">
    <div class="bg-n-solid-3 rounded-xl p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3
            class="text-lg font-semibold text-n-slate-12 flex items-center gap-2"
          >
            <span class="i-lucide-inbox size-5 text-woot-500" />
            {{ t('LIA_AGENTS.INBOXES.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mt-1">
            {{ t('LIA_AGENTS.INBOXES.SUBTITLE') }}
          </p>
        </div>

        <div v-if="!singleSelect" class="flex gap-2">
          <button
            type="button"
            class="text-xs text-woot-500 hover:text-woot-600"
            @click="selectAll"
          >
            {{ t('LIA_AGENTS.INBOXES.SELECT_ALL') }}
          </button>
          <span class="text-n-slate-9">|</span>
          <button
            type="button"
            class="text-xs text-n-slate-11 hover:text-n-slate-12"
            @click="deselectAll"
          >
            {{ t('LIA_AGENTS.INBOXES.CLEAR') }}
          </button>
        </div>
      </div>

      <!-- Selected count -->
      <div
        v-if="selectedIds.length > 0"
        class="mb-4 px-3 py-2 bg-woot-50 dark:bg-woot-900/20 rounded-lg"
      >
        <p class="text-sm text-woot-700 dark:text-woot-300">
          <span class="font-medium">{{ selectedIds.length }}</span>
          {{
            selectedIds.length === 1
              ? t('LIA_AGENTS.INBOXES.SELECTED_SINGULAR')
              : t('LIA_AGENTS.INBOXES.SELECTED_PLURAL')
          }}
        </p>
      </div>

      <!-- Inbox Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          v-for="inbox in inboxes"
          :key="inbox.id"
          class="flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all"
          :class="[
            selectedIds.includes(inbox.id)
              ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20'
              : 'border-n-weak hover:border-n-strong',
          ]"
          @click="toggleInbox(inbox.id)"
        >
          <div
            class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            :class="[
              selectedIds.includes(inbox.id)
                ? 'bg-woot-500 text-white'
                : 'bg-n-alpha-3',
            ]"
          >
            <span
              class="size-5"
              :class="[
                getInboxIcon(inbox),
                selectedIds.includes(inbox.id) ? '' : getInboxColor(inbox),
              ]"
            />
          </div>

          <div class="flex-1 min-w-0">
            <p class="font-medium text-n-slate-12 truncate">
              {{ inbox.name }}
            </p>
            <p class="text-xs text-n-slate-11 truncate">
              {{ inbox.channel_type }}
            </p>
          </div>

          <div
            v-if="selectedIds.includes(inbox.id)"
            class="w-6 h-6 rounded-full bg-woot-500 flex items-center justify-center flex-shrink-0"
          >
            <span class="i-lucide-check size-4 text-white" />
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="inboxes.length === 0" class="text-center py-8">
        <span class="i-lucide-inbox size-12 text-n-slate-9 mx-auto mb-3" />
        <p class="text-n-slate-11">
          {{ t('LIA_AGENTS.INBOXES.EMPTY_TITLE') }}
        </p>
        <p class="text-sm text-n-slate-9">
          {{ t('LIA_AGENTS.INBOXES.EMPTY_SUBTITLE') }}
        </p>
      </div>

      <!-- Warning if none selected -->
      <div
        v-if="selectedIds.length === 0 && inboxes.length > 0"
        class="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg"
      >
        <p
          class="text-sm text-amber-700 dark:text-amber-300 flex items-center gap-2"
        >
          <span class="i-lucide-alert-triangle size-4" />
          {{ t('LIA_AGENTS.INBOXES.WARNING') }}
        </p>
      </div>
    </div>
  </div>
</template>
